import { BasketItem, ExternalBasket } from '../types';

class AddToBasketApi {
    constructor(private readonly csrfToken: string) {
    }

    addItemPromise(productAlias: string, colour: string, size: string, quantity: number): Promise<ExternalBasket> {
        const fd = new FormData();
        fd.append('colour', colour);
        fd.append('size', size);
        fd.append('quantity', quantity.toString(10));

        return fetch('/basket/add/' + productAlias + '.json', {
            method: 'POST',
            credentials: 'same-origin',
            body: JSON.stringify({ colour, size, quantity }),
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': this.csrfToken,
            },
        }).then(resp => {
            if (resp.status !== 201) {
                throw 'Unable to add item to basket';
            }

            return resp.json() as Promise<ExternalBasket>;
        });
    }

    addManyItemsPromise(productAlias: string, basketItems: BasketItem[]): Promise<ExternalBasket> {
        const fd = new FormData();
        basketItems.forEach(bi => fd.append('quantity[' + bi.sku + ']', bi.quantity.toString(10)));

        return fetch('/basket/add-many/' + productAlias + '.json', {
            method: 'POST',
            credentials: 'same-origin',
            body: fd,
            headers: {
                Accept: 'application/json',
                'X-CSRF-TOKEN': this.csrfToken,
            },
        }).then(resp => {
            if (resp.status !== 201) {
                throw 'Unable to add items to basket';
            }

            return resp.json() as Promise<ExternalBasket>;
        });
    }

    removeItemPromise(sku: string): Promise<ExternalBasket> {
        return fetch('/basket/remove/' + sku + '.json', {
            method: 'POST',
            credentials: 'same-origin',
            body: JSON.stringify({ sku }),
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': this.csrfToken,
            },
        }).then(resp => {
            if (resp.status !== 200) {
                throw 'Unable to remove item from basket';
            }

            return resp.json() as Promise<ExternalBasket>;
        });
    }

    changeQuantityPromise(sku: string, quantity: number): Promise<ExternalBasket> {
        return fetch('/basket/update/' + sku + '.json', {
            method: 'POST',
            credentials: 'same-origin',
            body: JSON.stringify({ sku, quantity }),
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': this.csrfToken,
            },
        }).then(resp => {
            if (resp.status !== 200) {
                throw 'Unable to change the quantity of item in basket';
            }

            return resp.json() as Promise<ExternalBasket>;
        });
    }

    setDeliveryOptionPromise(optionId: number): Promise<ExternalBasket> {
        const fd = new FormData();
        fd.append('optionId', optionId.toString());

        return fetch('/basket/delivery.json', {
            method: 'POST',
            body: fd,
            credentials: 'same-origin',
            headers: {
                Accept: 'application/json',
                'X-CSRF-TOKEN': this.csrfToken,
            },
        }).then(resp => {
            if (resp.status !== 200) {
                throw 'Unable to set delivery option';
            }

            return resp.json() as Promise<ExternalBasket>;
        });
    }

    setOrderTypePromise(orderType: string | null): Promise<ExternalBasket> {
        const fd = new FormData();
        fd.append('orderType', (orderType === null) ? '' : orderType);

        return fetch('/basket/order-type.json', {
            method: 'POST',
            body: fd,
            credentials: 'same-origin',
            headers: {
                Accept: 'application/json',
                'X-CSRF-TOKEN': this.csrfToken,
            },
        }).then(resp => {
            if (resp.status !== 200) {
                throw 'Unable to set order type';
            }

            return resp.json() as Promise<ExternalBasket>;
        });
    }
}

const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

if (!csrfToken) {
    throw 'Unable to get CSRF token, basket API unavailable';
}

const api = new AddToBasketApi(csrfToken);

export default api;
